const PRODUCT_SELECT_EVENT = 'shopify:product:select';
const sectionGenerations = new WeakMap();

const ESTIMATES = {
  unframed: {
    key: 'unframed',
    label: 'Unframed art print',
    range: '4–5 working days',
  },
  framed: {
    key: 'framed',
    label: 'Framed or gallery-wrap artwork',
    range: '7–10 working days',
  },
  artwork: {
    key: 'artwork',
    label: 'Painting or mixed-media artwork',
    range: '25–30 working days',
  },
};

function normalizedValues(options) {
  return Array.isArray(options) ? options.map((value) => String(value).trim().toLowerCase()) : [];
}

function isFramedValue(value) {
  if (value.includes('unframed') || /^(no frame|without frame|print only)$/.test(value)) return false;
  return value.includes('gallery wrap') || value.includes('gallery-wrap') || value.includes('framed') || value.includes('frame');
}

export function selectProductionEstimate(family, options = []) {
  const normalizedFamily = String(family || '').trim().toLowerCase();
  if (normalizedFamily === 'painting' || normalizedFamily === 'mixed-media') return { ...ESTIMATES.artwork };
  if (normalizedFamily === 'frame' || normalizedFamily === 'gallery-wall') return { ...ESTIMATES.framed };
  if (normalizedFamily !== 'art-print') return null;
  return normalizedValues(options).some(isFramedValue) ? { ...ESTIMATES.framed } : { ...ESTIMATES.unframed };
}

export function isSixDigitPin(value) {
  return typeof value === 'string' && /^[0-9]{6}$/.test(value);
}

// 2026-09-23: Indian PIN codes are six digits and never start with 0 or 9
// (first digit = postal zone 1-8). This checks plausibility only; there is no
// carrier lookup behind this control, so the estimate below is the same for
// every valid PIN and is worded as a general estimate, not a per-PIN promise.
export function isPlausibleIndianPin(value) {
  return typeof value === 'string' && /^[1-8][0-9]{5}$/.test(value);
}

function addWorkingDays(startDate, days) {
  const date = new Date(Date.UTC(startDate.getUTCFullYear(), startDate.getUTCMonth(), startDate.getUTCDate()));
  for (let elapsed = 0; elapsed < days; ) {
    date.setUTCDate(date.getUTCDate() + 1);
    if (![0, 6].includes(date.getUTCDay())) elapsed += 1;
  }
  return date;
}

function formatArrivalDate(date) {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${date.getUTCDate()} ${months[date.getUTCMonth()]}`;
}

export function estimateArrivalWindow(estimateRange, startDate = new Date()) {
  const match = /^(\d+)\s*[–-]\s*(\d+)\s+working days$/.exec(String(estimateRange || '').trim());
  if (!match || !(startDate instanceof Date) || Number.isNaN(startDate.valueOf())) return null;
  return {
    earliest: formatArrivalDate(addWorkingDays(startDate, Number(match[1]) + 5)),
    latest: formatArrivalDate(addWorkingDays(startDate, Number(match[2]) + 7)),
  };
}

export function pinEstimateMessage(pin, estimateRange, startDate = new Date()) {
  pin = String(pin ?? '').trim();
  if (!isSixDigitPin(pin)) return 'Enter a valid six-digit PIN code.';
  if (!isPlausibleIndianPin(pin)) return `${pin} is not a valid Indian PIN code. Indian PIN codes are six digits starting with 1 to 8.`;
  if (!estimateRange) return 'PIN format confirmed. Final serviceability is confirmed at checkout.';
  const arrival = estimateArrivalWindow(estimateRange, startDate);
  if (!arrival) return 'PIN format confirmed. Final serviceability is confirmed at checkout.';
  const [earliestDay, earliestMonth] = arrival.earliest.split(' ');
  const [latestDay, latestMonth] = arrival.latest.split(' ');
  const arrivalLabel = earliestMonth === latestMonth
    ? `${earliestDay}–${latestDay} ${latestMonth}`
    : `${arrival.earliest}–${arrival.latest}`;
  return `Estimated arrival: ${arrivalLabel} (most of India). Allow ${estimateRange} for production, then usually 5–7 working days after dispatch. This is a general estimate, not specific to your PIN code — remote areas can take longer. Final serviceability is confirmed at checkout.`;
}

function formatVariantPrice(price, currency) {
  const paise = Number(price);
  if (!Number.isInteger(paise) || paise < 0 || !currency) return '';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(paise / 100);
}

function optionNames(root) {
  try {
    const names = JSON.parse(root?.dataset?.optionNames || '[]');
    return Array.isArray(names) ? names.map(String) : [];
  } catch {
    return [];
  }
}

function updatePurchaseInfo(root, resource) {
  if (!root) return;
  const selected = root.querySelector('[data-sa-selected-options]');
  const availability = root.querySelector('[data-sa-availability]');

  if (!resource || resource.available === false) {
    if (selected) selected.textContent = '';
    if (availability) {
      availability.textContent = resource?.available === false ? 'Currently unavailable.' : 'Choose an available combination.';
    }
    return;
  }

  const values = Array.isArray(resource.options) ? resource.options : [];
  const summary = optionNames(root)
    .map((name, index) => ({ name, value: values[index] }))
    .filter(({ name, value }) => value != null && value !== '' && !(name === 'Title' && value === 'Default Title'))
    .map(({ name, value }) => `${name}: ${value}`)
    .join(' · ');

  if (selected) selected.textContent = summary;
  if (availability) availability.textContent = 'Made to order · Available';
}

function updateTimeline(root, resource) {
  if (!root) return;
  const estimate = resource?.available === false || !resource
    ? null
    : selectProductionEstimate(root.dataset.family, resource.options);
  const label = root.querySelector('[data-sa-production-label]');
  const range = root.querySelector('[data-sa-production-range]');
  const copy = root.querySelector('[data-sa-production-copy]');
  const empty = root.querySelector('[data-sa-production-empty]');
  const pinStatus = root.querySelector('[data-sa-pin-status]');

  root.dataset.estimateRange = estimate?.range ?? '';
  if (label) label.textContent = estimate?.label ?? '';
  if (range) range.textContent = estimate?.range ?? '';
  if (copy) copy.hidden = !estimate;
  if (empty) empty.hidden = Boolean(estimate);
  if (pinStatus) pinStatus.textContent = '';
}

function updateActionPrices(section, purchase, resource) {
  const price = resource?.available === false || !resource
    ? ''
    : formatVariantPrice(resource.price, purchase?.dataset.currency);
  for (const node of section.querySelectorAll?.('[data-sa-action-price]') ?? []) node.textContent = price;
}

export function updateProductSection(section, detail) {
  if (!section || !detail) return false;
  const purchase = section.querySelector('[data-sa-purchase-info]');
  const timelines = [...(section.querySelectorAll?.('[data-sa-delivery-root]') ?? [])];
  const expectedProductId = purchase?.dataset.productId || timelines[0]?.dataset.productId;
  if (detail.productId && expectedProductId && String(detail.productId) !== String(expectedProductId)) return false;

  updatePurchaseInfo(purchase, detail.resource ?? null);
  for (const timeline of timelines) updateTimeline(timeline, detail.resource ?? null);
  updateActionPrices(section, purchase, detail.resource ?? null);
  return Boolean(purchase || timelines.length);
}

export async function handleProductSelect(event) {
  const target = event?.target;
  if (!target?.closest || target.closest('product-card')) return false;
  const section = target.closest('.shopify-section, dialog');
  if (!section || !event.promise?.then) return false;

  const generation = (sectionGenerations.get(section) || 0) + 1;
  sectionGenerations.set(section, generation);

  try {
    const result = await event.promise;
    if (sectionGenerations.get(section) !== generation) return false;
    return updateProductSection(section, result?.detail);
  } catch (error) {
    if (error?.name !== 'AbortError') console.warn('[delivery-timeline] Product selection failed:', error);
    return false;
  }
}

function handlePinCheck(event) {
  const button = event.target?.closest?.('[data-sa-pin-check]');
  if (!button) return;
  runPinCheck(button);
}

// Enter in the PIN field runs the check, same as the button (the field is not in a form).
function handlePinKeydown(event) {
  if (event.key !== 'Enter' || event.isComposing) return;
  const input = event.target?.closest?.('[data-sa-pin]');
  if (!input) return;
  const button = input.closest('[data-sa-delivery-root]')?.querySelector('[data-sa-pin-check]');
  if (!button) return;
  event.preventDefault();
  runPinCheck(button);
}

function runPinCheck(button) {
  const root = button.closest('[data-sa-delivery-root]');
  const input = root?.querySelector('[data-sa-pin]');
  const status = root?.querySelector('[data-sa-pin-status]');
  if (!input || !status) return;

  status.textContent = pinEstimateMessage(input.value, root.dataset.estimateRange || '');
}

if (typeof document !== 'undefined' && typeof document.addEventListener === 'function') {
  document.addEventListener(PRODUCT_SELECT_EVENT, handleProductSelect);
  document.addEventListener('click', handlePinCheck);
  document.addEventListener('keydown', handlePinKeydown);
}
